/**
 * Authenticator app (TOTP) pages - replace allauth's mfa/totp/activate_form.html
 * and mfa/totp/deactivate_form.html
 */
import { Box, TextField } from "@mui/material";
import { AuthLayout, BRAND_COLORS } from "../components/AuthLayout";
import {
  CodeField,
  DangerButton,
  MFA_URLS,
  MfaBackLink,
  MfaContent,
  MfaMessages,
  MfaText,
  PostForm,
  PrimaryButton,
} from "../components/MfaParts";
import { useAuthContext, useFieldErrors } from "../hooks/useAuthContext";
import { gettext } from "@services/i18n";

const ACTIVATE_TITLE = gettext("Activate authenticator app");
const STEP_SCAN = gettext(
  "1. Scan this QR code with your authenticator app, e.g. Microsoft Authenticator or Google Authenticator.",
);
const STEP_ENTER_CODE = gettext(
  "2. Enter the 6-digit code the app shows for Captured Knowledge.",
);
const QR_CODE_ALT = gettext("QR code for your authenticator app");
const SECRET_LABEL = gettext("Can't scan the code? Enter this key instead");
const SECRET_HELP = gettext(
  "Keep this key private. You can use it to set up your authenticator app again later.",
);
const AUTHENTICATOR_CODE = gettext("Authenticator code");
const ACTIVATE = gettext("Activate");

const DEACTIVATE_TITLE = gettext("Deactivate authenticator app");
const DEACTIVATE_TEXT = gettext(
  "After deactivating, you sign in with your password only and your recovery codes stop working. Are you sure?",
);
const DEACTIVATE = gettext("Deactivate");

export function TotpActivatePage() {
  const { totpQrCodeDataUri, totpSecret } = useAuthContext();
  const codeErrors = useFieldErrors("code");

  return (
    <AuthLayout title={ACTIVATE_TITLE}>
      <MfaContent>
        <MfaMessages />
        <MfaText>{STEP_SCAN}</MfaText>
        <Box
          sx={(theme) => ({
            display: "flex",
            justifyContent: "center",
            marginBottom: theme.spacing(2),
          })}
        >
          <Box
            component="img"
            src={totpQrCodeDataUri}
            alt={QR_CODE_ALT}
            sx={{
              width: 200,
              height: 200,
              border: `1px solid ${BRAND_COLORS.border}`,
              borderRadius: 2,
              padding: 1,
            }}
            data-testid="mfa-totp-qr-code"
          />
        </Box>
        <TextField
          fullWidth
          size="small"
          label={SECRET_LABEL}
          value={totpSecret ?? ""}
          helperText={SECRET_HELP}
          sx={(theme) => ({ marginBottom: theme.spacing(3) })}
          slotProps={{
            htmlInput: {
              readOnly: true,
              spellCheck: false,
              style: { fontFamily: "monospace", letterSpacing: "0.05em" },
              "data-testid": "mfa-totp-secret",
            },
          }}
        />
        <MfaText>{STEP_ENTER_CODE}</MfaText>
        <PostForm action={MFA_URLS.activateTotp}>
          <CodeField
            errors={codeErrors}
            label={AUTHENTICATOR_CODE}
            autoFocus={codeErrors.length > 0}
          />
          <PrimaryButton type="submit" data-testid="mfa-activate-submit">
            {ACTIVATE}
          </PrimaryButton>
        </PostForm>
        <MfaBackLink to="index" />
      </MfaContent>
    </AuthLayout>
  );
}

export function TotpDeactivatePage() {
  const { formErrors } = useAuthContext();
  // allauth validates the (input-less) form on GET, so a refusal is already in formErrors.
  const canDeactivate = Object.keys(formErrors).length === 0;

  return (
    <AuthLayout title={DEACTIVATE_TITLE}>
      <MfaContent>
        <MfaMessages />
        {canDeactivate && (
          <>
            <MfaText>{DEACTIVATE_TEXT}</MfaText>
            <PostForm action={MFA_URLS.deactivateTotp}>
              <DangerButton type="submit" data-testid="mfa-deactivate-submit">
                {DEACTIVATE}
              </DangerButton>
            </PostForm>
          </>
        )}
        <MfaBackLink to="index" />
      </MfaContent>
    </AuthLayout>
  );
}
